
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getUserFromSession } from '../../authOperations';
import { User } from '../../types';

interface AuthChangeListenerProps {
  checkSession: (force?: boolean) => Promise<void>;
  setUser: (user: User | null) => void;
  setSession: (session: any) => void;
  setIsLoading: (isLoading: boolean) => void;
  setLastError: (error: Error | null) => void;
  isMountedRef: React.MutableRefObject<boolean>;
}

export function useAuthChangeListener({
  checkSession,
  setUser,
  setSession,
  setIsLoading,
  setLastError,
  isMountedRef
}: AuthChangeListenerProps) {
  // Setup auth state subscription on component mount
  useEffect(() => {
    // Initial session check
    checkSession(true);
    
    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log('Auth state changed:', event);
      
      if (!isMountedRef.current) return;
      
      if (event === 'SIGNED_IN' && newSession) {
        console.log('User signed in event detected');
        setSession(newSession);
        setIsLoading(true);
        
        try {
          const userProfile = await getUserFromSession(newSession);
          
          if (!isMountedRef.current) return;
          
          if (userProfile) {
            console.log("User signed in:", userProfile.email);
            setUser(userProfile);
          } else {
            console.warn("No user profile after sign in");
            
            // Create a fallback user if we can't get the profile
            if (newSession.user) {
              const fallbackUser: User = {
                ...newSession.user,
                name: newSession.user.email?.split('@')[0] || 'User',
                subscriptionTier: 'free',
                profileImage: null,
                // Ensure provider is one of the allowed types
                provider: (newSession.user.app_metadata?.provider === 'google' || 
                          newSession.user.app_metadata?.provider === 'apple') ? 
                          newSession.user.app_metadata.provider as 'google' | 'apple' : 'email'
              };
              setUser(fallbackUser);
            }
          }
        } catch (error) {
          if (!isMountedRef.current) return;
          
          console.error('Error getting user profile:', error);
          setLastError(error instanceof Error ? error : new Error('Profile fetch error'));
        } finally {
          if (isMountedRef.current) {
            // Small delay for better UX
            setTimeout(() => {
              setIsLoading(false);
            }, 100);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        console.log("User signed out");
        setUser(null);
        setSession(null);
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED') {
        console.log("Token refreshed");
        if (newSession) {
          setSession(newSession);
        }
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [checkSession, setIsLoading, setLastError, setSession, setUser, isMountedRef]);
}
